import { useState } from 'react'
import { Paperclip, FileText, CheckCircle } from 'lucide-react'
import { useOrgPath } from '@/lib/pathFor'
import { formatDateTime, extractSenderName, extractEmail, getInitials } from '@/lib/formatters'

interface TimelineEntry {
  id: string
  type: 'inbound' | 'outbound'
  from: string
  to: string | null
  date: string | null
  body: string | null
  summary?: string | null
  sent_at?: string | null
  status: string
  attachments?: Array<{ id: string; filename: string; mime_type: string; size: number }>
}

function AttachmentList({ attachments }: { attachments: { id: string; filename: string }[] }) {
  const pathFor = useOrgPath()
  if (!attachments || attachments.length === 0) return null

  return (
    <div className="border-t border-border pt-3 space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        <Paperclip className="h-3 w-3" />
        <span>{attachments.length} attachment{attachments.length !== 1 ? 's' : ''}</span>
      </div>
      {attachments.map((att) => (
        <div key={att.id} className="flex items-center gap-2 text-[13px]">
          <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <a
            href={pathFor(`/attachments/${att.id}/download`)}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-primary hover:underline cursor-pointer"
          >
            {att.filename}
          </a>
        </div>
      ))}
    </div>
  )
}

export default function EmailEntry({ entry, isLatest }: { entry: TimelineEntry; isLatest: boolean }) {
  const [expanded, setExpanded] = useState(isLatest)

  const isOutbound = entry.type === 'outbound'
  const timestamp = isOutbound
    ? (entry.sent_at ? formatDateTime(entry.sent_at) : entry.date ? formatDateTime(entry.date) : '')
    : (entry.date ? formatDateTime(entry.date) : '')

  return (
    <div className={`rounded-lg border p-4 space-y-3 ${
      isOutbound
        ? 'border-green-200 bg-green-50/50'
        : isLatest
          ? 'bg-background border-border'
          : 'bg-muted/30 border-border'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          {isOutbound ? (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-700 text-xs font-semibold">
              <CheckCircle className="h-4 w-4" />
            </div>
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              {getInitials(extractSenderName(entry.from || ''))}
            </div>
          )}
          <div>
            {isOutbound ? (
              <>
                <span className="text-[13px] font-semibold text-foreground">Sent</span>
                <span className="ml-1.5 text-xs text-muted-foreground">to {entry.to}</span>
              </>
            ) : (
              <>
                <span className="text-[13px] font-semibold text-foreground">
                  {extractSenderName(entry.from || '')}
                </span>
                <span className="ml-1.5 text-xs text-muted-foreground">
                  {extractEmail(entry.from || '')}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isLatest && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-muted-foreground hover:text-foreground underline cursor-pointer"
            >
              {expanded ? 'Collapse' : 'Expand'}
            </button>
          )}
          <span className="text-xs text-muted-foreground">{timestamp}</span>
        </div>
      </div>

      {/* Summary */}
      {entry.summary && (
        <div className="text-xs text-muted-foreground bg-gray-100 rounded px-2 py-1.5">
          <span className="font-medium">Summary:</span> {entry.summary}
        </div>
      )}

      {/* Body + attachments */}
      {expanded && (
        <>
          {entry.body && (
            <div className="text-[13px] leading-[1.6] text-foreground whitespace-pre-wrap">
              {entry.body}
            </div>
          )}
          <AttachmentList attachments={entry.attachments || []} />
        </>
      )}
    </div>
  )
}
